import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
  Avatar,
  Paper,
  Typography,
  InputAdornment,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme, alpha } from '@mui/material/styles';
import { BadgeOutlined, Search } from '@mui/icons-material';
import { api } from '../../services/api';
import { useToast } from "../../components/ToastContext";
import SearchComponent from '../../components/Search';
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';

const imageFormatDetectors = [
  { pattern: /^blob:|^http:|^https:|^data:/, handler: src => src },
  { pattern: /^iVBORw0K/, mimeType: 'image/png' },
  { pattern: /^\/9j\//, mimeType: 'image/jpeg' },
  { pattern: /^R0lGOD/, mimeType: 'image/gif' },
  { pattern: /^PHN2Zy/, mimeType: 'image/svg+xml' },
];

const detectImageFormat = (src) => {
  for (const detector of imageFormatDetectors) {
    if (detector.pattern.test(src)) {
      return detector.handler ? detector.handler(src) : `data:${detector.mimeType};base64,${src}`;
    }
  }
  return `data:image/png;base64,${src}`;
};

const getImageSrc = (input) => {
  if (!input) return "";
  return detectImageFormat(input.trim());
};

const ClientSetup = () => {
  const theme = useTheme();
  const toast = useToast();
  // Status filter driven by the header dropdown ('all' | 'active' | 'inactive')
  const [statusFilter, setStatusFilter] = useState('all');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientNameError, setClientNameError] = useState("");
  const [formData, setFormData] = useState({
    clientName: '',
    logo: null,
    logoPreview: '',
    isActive: true,
  });

  // Fetch clients
  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/Client/GetAllClientDetails`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error(error.message || 'Failed to fetch clients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    return () => {
      if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }
    };
  }, [formData.logoPreview]);


  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleAddNew = () => {
    setSelectedClient(null);
    setFormData({
      clientName: '',
      logo: null,
      logoPreview: '',
      isActive: true,
    });
    setOpenDialog(true);
  };

  const handleEdit = async (clientId) => {
    try {
      const response = await api.get(`/Client/GetClientDetailsByClientId/${clientId}`, {
        headers: {
          'X-CORRELATION-ID': '4fa84814-7ba6-4cbc-b762-c006aa8f57c4',
        },
      });

      const clientData = response.data;


      setSelectedClient({
        ...clientData,
        id: clientData.client_Id,
      });


      setFormData({
        clientName: clientData.client_Name || '',
        logo: clientData.logo,
        logoPreview: clientData.logo || '',
        isActive: clientData.isActive !== undefined ? clientData.isActive : true,
      });
      setClientNameError("");
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching client for edit:', error);
      toast.error(error.message || 'Failed to fetch client details for editing.');
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedClient(null);
    if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.logoPreview);
    }
    setFormData({
      clientName: '',
      logo: null,
      logoPreview: '',
      isActive: true,
    });
    setClientNameError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }

      setFormData({
        ...formData,
        logo: file,
        logoPreview: URL.createObjectURL(file),
      });
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const buildClientPayload = async (formData, userId) => {
    let logoBase64 = null;
    if (formData.logo && formData.logo instanceof File) {
      logoBase64 = await fileToBase64(formData.logo);
    }

    return {
      client_Name: formData.clientName.trim(),
      logo: logoBase64 || formData.logo || "",
      isActive: formData.isActive,
      createdBy: userId ? Number.parseInt(userId) : 0,
      updatedBy: userId ? Number.parseInt(userId) : 0,
    };
  };

  const handleSave = async () => {
    setClientNameError("");
    
    if (!formData.clientName.trim()) {
      const requiredMessage = 'Client name is required';
      toast.error(requiredMessage);
      setClientNameError(requiredMessage); // <-- Added here for consistency
      return;
    }

    try {
      const userId = sessionStorage.getItem('userId');
      const payload = await buildClientPayload(formData, userId);

      let response;
      if (selectedClient) {
        response = await api.post(
          `/Client/UpdateClientDetails/${selectedClient.id}`,
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CORRELATION-ID': '4fa84814-7ba6-4cbc-b762-c006aa8f57c4',
            },
          }
        );

        if (response.status === 200) {
          await fetchClients();
          toast.success('Client updated successfully.');
          handleDialogClose();
        }
      } else {
        response = await api.post(
          '/Client/CreateClientDetails',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CORRELATION-ID': '4fa84814-7ba6-4cbc-b762-c006aa8f57c4',
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          await fetchClients();
          toast.success('Client added successfully.');
          handleDialogClose();
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      const errorMessage = error.response?.data?.errorMessages?.[0]?.message || 'An error occurred while saving the client.';
      
      toast.error(errorMessage);
      setClientNameError(errorMessage);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'client_Name',
      headerName: 'Client Name',
      flex: 1,
      renderCell: (params) => params.row?.client_Name || '',
    },
    {
      field: 'logo',
      headerName: 'Logo',
      flex: 1,
      renderCell: (params) => {
        const logoSrc = params.row?.logo ? getImageSrc(params.row.logo) : '';
        return (
          <Avatar
            variant="square"
            src={logoSrc}
            alt={params.row?.client_Name || 'Logo'}
            sx={{
              width: 40,
              height: 40,
              '& .MuiAvatar-img': { objectFit: 'contain' }
            }}
          />
        )
      }
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.7,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Map the boolean to the readable label so search matches on it
      valueGetter: (value, row) => (row?.isActive ? "Active" : "Inactive"),
      // Heading with a filter icon that opens a simple Active/Inactive dropdown
      renderHeader: () => (
        <StatusFilterHeader value={statusFilter} onChange={setStatusFilter} />
      ),
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            mt: 1.1,
            px: 1.4,
            py: 0.55,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
            bgcolor: alpha(
              params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
              theme.palette.mode === "dark" ? 0.18 : 0.12
            ),
          }}
        >
          {params.row?.isActive ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',

      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEdit(params.row.client_Id)}
          sx={{ borderRadius: 999, px: 1.8 }}
        >
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, statusFilter]);

  // Search across all data columns, then apply the Status dropdown filter
  const filteredClients = useMemo(
    () => applyStatusFilter(filterRowsByColumns(clients, searchTerm, columns), statusFilter),
    [clients, searchTerm, columns, statusFilter]
  );

  return (
    <Box
      sx={{
        flex: 1,
        color: theme.palette.text.primary,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1), color: theme.palette.primary.main }}>
              <BadgeOutlined />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
              Clients
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SearchComponent
              value={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder="Search clients..."
              sx={{ mb: theme.spacing(2) }}
            />
          <Button
              variant="contained"
              sx={{
                color: theme.palette.common.white,
                bgcolor: theme.palette.primary.main,
                borderRadius: 2,
                px: 2.4,
                py: 1.1,
              }}
              onClick={handleAddNew}
            >
              Add New Client
            </Button>
          </Box>
        </Box>




        <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : <DataGrid
            rows={filteredClients}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 15 },
              },
            }}
            pageSizeOptions={[5, 10, 20]}
            disableSelectionOnClick
            hideFooterSelectedRowCount
            getRowId={(row) => row.client_Id}
            sx={{
              border: "none",
              bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",

              "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": {
                color: (t) => `${t.palette.primary.main} !important`,
              },

              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
              },

              "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": {
                color: (t) => `${t.palette.action.disabled} !important`,
              },

              "--DataGrid-rowBorderColor": "transparent",

              "& .MuiDataGrid-filler": { display: "none" },

              "& .MuiDataGrid-columnHeaders": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                color: "text.primary",
                borderBottom: "none",
              },

              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold !important",
                fontSize: 12,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              },

              "& .MuiDataGrid-columnSeparator": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": {
                color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)",
              },

              "& .MuiDataGrid-cell": {
                color: "text.primary",
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa",
              },

              "& .MuiDataGrid-row:nth-of-type(odd)": {
                bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff",
              },

              "& .MuiDataGrid-row:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important",
              },

              "& .MuiDataGrid-row.Mui-selected": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important",
              },

              "& .MuiDataGrid-row.Mui-selected:hover": {
                bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important",
              },

              "& .MuiDataGrid-footerContainer": {
                bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff",
                borderTop: "none",
                color: "text.primary",
              },

              "& .floating-actions, & .row-actions": {
                backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(40, 40, 40, 0.9)" : "#e8f0fe",
                flex: 1,
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                gap: "6px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.2s ease-in-out",
                padding: "4px 8px",
                borderRadius: "4px",
              },

              "& .MuiDataGrid-row:hover .floating-actions, & .MuiDataGrid-row:hover .row-actions, & .MuiDataGrid-row.Mui-selected .floating-actions, & .MuiDataGrid-row.Mui-selected .row-actions": {
                opacity: 1,
                pointerEvents: "auto",
              },

              "& .MuiDataGrid-cell[data-field='__floating_actions'], & .MuiDataGrid-cell[data-field='actions']": {
                overflow: "visible !important",
                width: "0px !important",
                padding: 0,
                borderBottom: "none !important",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none"
              },
            }}
          />}
        </Box>

        {/* Add/Edit Client Dialog */}
        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ bgcolor: theme.palette.background.default }}>
            {selectedClient ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
          <DialogContent sx={{ bgcolor: theme.palette.background.paper, py: 2 }}>
            <TextField
              margin="dense"
              label="Client Name"
              type="text"
              fullWidth
              required
              value={formData.clientName}
              onChange={(e) => {
                setFormData({ ...formData, clientName: e.target.value });
                if (clientNameError) setClientNameError("");
              }}
              error={Boolean(clientNameError)}
              helperText={clientNameError}
              sx={{ mb: 2, mt: 4 }}
            />

            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained" color="primary"
                component="label"
                sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
              >
                {formData?.logo?.name ? formData.logo.name : (selectedClient ? 'Change Logo (Image file, max 5MB)' : 'Select Logo (Image file, max 5MB)')}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>

            {formData.logoPreview && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar
                  variant="square"
                  src={getImageSrc(formData.logoPreview)}
                  alt="Logo Preview"
                  sx={{
                    width: 'auto',
                    height: 100,
                    '& .MuiAvatar-img': { objectFit: 'contain' }
                  }}
                />
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, logo: formData.logo, isActive: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="Active"
            />
          </DialogContent>
          <DialogActions sx={{ bgcolor: theme.palette.background.default, p: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              sx={{
                p: 0.7,
                px: 2,
                color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.secondary',
                borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                  borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
                }
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" sx={{ py: 1.2 }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ClientSetup;